/**
 * Privacy Policy Page (GDPR Compliant)
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../../components/layout/PublicLayout';

export default function PrivacyPolicyPage() {
  const { t } = useTranslation('legal');
  const lastUpdated = '2024-12-07';

  return (
    <PublicLayout bgClass="bg-gray-50">
      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('privacy.title')}</h1>
          <p className="text-sm text-gray-500 mb-8">{t('privacy.lastUpdated')}: {lastUpdated}</p>

          <div className="prose prose-blue max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section1.title')}</h2>
              <p className="text-gray-600 mb-4">{t('privacy.section1.p1')}</p>
              <p className="text-gray-600">{t('privacy.section1.p2')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section2.title')}</h2>
              <p className="text-gray-600 mb-4">{t('privacy.section2.p1')}</p>
              <p className="text-gray-600">
                {t('privacy.section2.contactEmail')}{' '}
                <a href="mailto:privacy@example.com" className="text-blue-600 hover:underline">privacy@example.com</a>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section3.title')}</h2>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t('privacy.section3.accountInfo')}</h3>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('privacy.section3.accountInfoList', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t('privacy.section3.usageData')}</h3>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('privacy.section3.usageDataList', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t('privacy.section3.formData')}</h3>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('privacy.section3.formDataList', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section4.title')}</h2>
              <p className="text-gray-600 mb-4">{t('privacy.section4.p1')}</p>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('privacy.section4.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section5.title')}</h2>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('privacy.section5.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section6.title')}</h2>
              <p className="text-gray-600 mb-4">{t('privacy.section6.p1')}</p>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('privacy.section6.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section7.title')}</h2>
              <p className="text-gray-600 mb-4">{t('privacy.section7.p1')}</p>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('privacy.section7.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-600 mt-4">{t('privacy.section7.p2')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section8.title')}</h2>
              <p className="text-gray-600 mb-4">{t('privacy.section8.p1')}</p>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('privacy.section8.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-600 mt-4">
                {t('privacy.section8.exerciseRights')}{' '}
                <Link to="/settings/privacy" className="text-blue-600 hover:underline">{t('privacy.section8.privacySettings')}</Link>{' '}
                {t('privacy.section8.orContactUs')}{' '}
                <a href="mailto:privacy@example.com" className="text-blue-600 hover:underline">privacy@example.com</a>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section9.title')}</h2>
              <p className="text-gray-600 mb-4">{t('privacy.section9.p1')}</p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t('privacy.section9.essential')}</h3>
              <p className="text-gray-600 mb-4">{t('privacy.section9.essentialText')}</p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t('privacy.section9.functional')}</h3>
              <p className="text-gray-600 mb-4">{t('privacy.section9.functionalText')}</p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">{t('privacy.section9.analytics')}</h3>
              <p className="text-gray-600 mb-4">{t('privacy.section9.analyticsText')}</p>

              <p className="text-gray-600">
                {t('privacy.section9.manageCookies')}{' '}
                <Link to="/settings/privacy" className="text-blue-600 hover:underline">{t('privacy.section8.privacySettings')}</Link>.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section10.title')}</h2>
              <p className="text-gray-600 mb-4">{t('privacy.section10.p1')}</p>
              <ul className="list-disc ltr:pl-6 rtl:pr-6 text-gray-600 space-y-2">
                {(t('privacy.section10.list', { returnObjects: true }) as string[]).map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
              <p className="text-gray-600 mt-4">{t('privacy.section10.p2')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section11.title')}</h2>
              <p className="text-gray-600">{t('privacy.section11.p1')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section12.title')}</h2>
              <p className="text-gray-600">{t('privacy.section12.p1')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section13.title')}</h2>
              <p className="text-gray-600">{t('privacy.section13.p1')}</p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('privacy.section14.title')}</h2>
              <p className="text-gray-600 mb-4">{t('privacy.section14.p1')}</p>
              <p className="text-gray-600 mb-4">
                {t('privacy.section14.email')}{' '}
                <a href="mailto:privacy@example.com" className="text-blue-600 hover:underline">privacy@example.com</a>
              </p>
              <p className="text-gray-600">{t('privacy.section14.p2')}</p>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 flex justify-between">
            <Link to="/" className="text-blue-600 hover:underline">
              &larr; {t('privacy.backToHome')}
            </Link>
            <Link to="/terms" className="text-blue-600 hover:underline">
              {t('privacy.termsOfService')} &rarr;
            </Link>
          </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
